import { useMutation } from "@tanstack/react-query";
import { useClientContext } from "clients";
import { GetReportResponse } from "services/management-backend/client";

interface GenerateReportResponse {
  id: string;
  url?: string;
}

const useGenerateAndGetReport = () => {
  const clients = useClientContext();
  const POLL_INTERVAL = 5000; // 5 seconds
  const TIMEOUT = 60000; // 1 minute max polling time

  return useMutation<
    GenerateReportResponse,
    Error,
    {
      account: string;
      year: number;
      reportType: string;
      month: number;
      id?: string | null;
    }
  >({
    mutationFn: async ({ account, year, reportType, month, id }) => {
      let reportId = id;

      if (!reportId) {
        // Generate a new report if no ID is passed
        const response = await clients.management.postGenerateReport(
          account,
          year,
          reportType,
          month
        );

        if (!response.id) {
          throw new Error("No ID received for the generated report.");
        }
        reportId = response.id;
      }

      return new Promise<GenerateReportResponse>(async (resolve, reject) => {
        const startTime = Date.now();

        const poll = async () => {
          try {
            const statusResponse: GetReportResponse =
              await clients.management.getReport(reportId as string);

            if (statusResponse.url) {
              const downloadReport = document.createElement('a')
              downloadReport.href = statusResponse.url
              downloadReport.download = 'downloaded_file'
              downloadReport.style.display = 'none'
              document.body.appendChild(downloadReport)
              downloadReport.click()
              document.body.removeChild(downloadReport)
              resolve({ id: reportId as string, url: statusResponse.url });
              return;
            }

            if (Date.now() - startTime >= TIMEOUT) {
              reject(new Error("Report generation timed out."));
              return;
            }

            setTimeout(poll, POLL_INTERVAL);
          } catch (error) {
            reject(new Error("Failed to fetch report."));
          }
        };

        poll();
      });
    },
    onError: (error) => {
      console.error("Failed to generate or fetch report:", error);
    },
  });
};

export default useGenerateAndGetReport;
